import type { AuthenticatedUserResponseDto } from '@monksflow/platform-api-sdk';
import { create } from 'zustand';
import { updateFeatureFlagWorkspace } from '@/shared/config/feature-flag-provider';

const WORKSPACE_LOCAL_STORAGE_KEY = 'mf_workspace';

interface WorkspaceByOrg {
  [organizationId: string]: string; // organizationId -> workspaceId
}

const getStoredWorkspaceData = (): WorkspaceByOrg => {
  try {
    const stored = localStorage.getItem(WORKSPACE_LOCAL_STORAGE_KEY);
    if (!stored) return {};

    return JSON.parse(stored) as WorkspaceByOrg;
  } catch {
    // If parsing fails (old string format), return empty object
    return {};
  }
};

const getWorkspaceForOrg = (organizationId: string): string | null => {
  const data = getStoredWorkspaceData();
  return data[organizationId] ?? null;
};

const setWorkspaceForOrg = (workspaceId: string, organizationId: string): void => {
  const data = getStoredWorkspaceData();
  data[organizationId] = workspaceId;
  localStorage.setItem(WORKSPACE_LOCAL_STORAGE_KEY, JSON.stringify(data));
};

const useWorkspaceStore = create<{
  workspaceId: string | null;
  setWorkspaceId: (workspaceId: string, orgId: string) => void;
}>((set) => ({
  workspaceId: null,
  setWorkspaceId: (workspaceId: string, orgId: string) => {
    setWorkspaceForOrg(workspaceId, orgId);
    set({ workspaceId });
    void updateFeatureFlagWorkspace(workspaceId);
  },
}));

// Hook to get current workspace ID
export const useCurrentWorkspaceId = () => {
  return useWorkspaceStore((state) => state.workspaceId);
};

// Direct Access API for backwards compatibility
export const getWorkspaceId = () => {
  return useWorkspaceStore.getState().workspaceId;
};

/**
 * Saves the chosen workspace for the next page load, without changing the
 * current one. Call it right before a full reload: changing the current
 * workspace would make the page you are leaving redraw, and an entity page
 * would then switch the workspace back before the reload finishes.
 */
export const saveWorkspaceForNextLoad = (workspaceId: string, orgId: string): void => {
  setWorkspaceForOrg(workspaceId, orgId);
};

// Wrapped to always dereference the current store setter at call time.
// Avoids capturing a stale reference if the store is ever re-created
// (HMR, test isolation, store.destroy()).
export const setWorkspaceId = (workspaceId: string, orgId: string): void => {
  useWorkspaceStore.getState().setWorkspaceId(workspaceId, orgId);
};

/**
 * URL query param that makes boot select a specific workspace (priority 1 in
 * `initializeWorkspace`). Exported so writers of deep-link URLs (e.g. the
 * cross-org bounce in `useProjectResolution`) share this symbol with the
 * reader below instead of re-hardcoding the string.
 */
export const WORKSPACE_SLUG_KEY = 'monksFlowWorkspaceSlug';

const getPlatformWorkspaceId = (user: AuthenticatedUserResponseDto): string | null => {
  const url = new URL(location.href);
  const platformWorkspaceSlug = url.searchParams.get(WORKSPACE_SLUG_KEY);

  if (!platformWorkspaceSlug) return null;

  return user.workspaces.find((ws) => ws.slug === platformWorkspaceSlug)?.id ?? null;
};

/**
 * URL query param carrying an explicit workspace id. The headless PDF renderer
 * navigates to `/download/:type/:id?workspaceId=…` in a fresh browser context
 * (no localStorage, no slug), so this is the ONLY signal that gets the right
 * workspace into the store — which the `/download` route's feature-flag gate and
 * the workspace-id request interceptor both read. Validated against the user's
 * own workspaces so a URL can never select a workspace the caller isn't in.
 */
export const WORKSPACE_ID_KEY = 'workspaceId';

const getExplicitWorkspaceId = (user: AuthenticatedUserResponseDto): string | null => {
  const requested = new URL(location.href).searchParams.get(WORKSPACE_ID_KEY);
  if (!requested) return null;

  return user.workspaces.find((ws) => ws.id === requested)?.id ?? null;
};

/**
 * Reads the last-used workspace for the org, discarding it when it is no longer
 * one of the user's workspaces.
 */
const getValidStoredWorkspaceId = (user: AuthenticatedUserResponseDto): string | null => {
  const stored = getWorkspaceForOrg(user.organization.id);
  if (!stored) return null;

  return user.workspaces.some((ws) => ws.id === stored) ? stored : null;
};

export const initializeWorkspace = (user: AuthenticatedUserResponseDto) => {
  const orgId = user.organization.id;

  // Priority 1: explicit ?workspaceId= (validated), then ?monksFlowWorkspaceSlug=
  let workspaceId = getExplicitWorkspaceId(user) ?? getPlatformWorkspaceId(user);

  // Priority 2: localStorage (validated against the user's current workspaces)
  if (!workspaceId) {
    workspaceId = getValidStoredWorkspaceId(user);
  }

  // Priority 3: First workspace as fallback
  if (!workspaceId && user.workspaces.length > 0) {
    workspaceId = user.workspaces[0].id;
  }

  if (!workspaceId) {
    throw new Error('No workspace ID found for user');
  }

  setWorkspaceId(workspaceId, orgId);
};
